import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, List, ListItem, ListItemButton, ListItemText, Paper, CircularProgress, Alert } from '@mui/material';
import { Folder as FolderIcon, InsertDriveFile as FileIcon, ChevronRight as ChevronRightIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { apiService } from '../services/api';
import {
	buildRootNodes,
	buildFileTree,
	mergeTree,
	updateNodeExpanded,
	setNodeChildren,
	findNodeByPath,
	sortWorkspaceFiles,
} from './fileTreeUtils';
import type { FileNode, WorkspaceFile } from './fileTreeUtils';

interface MetadataBrowserProps {
	orgId: string;
	workspaceId: string;
	containerAppUrl: string;
	selectedPath: string;
	onPathSelect: (path: string) => void;
	currentUserId: string | null;
}

/**
 * Filters the flat file list so that entries under output-docs/ only appear
 * if they belong to the current user. Other users' subdirectories are hidden.
 * Entries outside output-docs/ are always included.
 */
function filterOutputDocsForUser(files: WorkspaceFile[], currentUserId: string | null): WorkspaceFile[] {
	return files.filter(file => {
		const normalizedPath = file.path.replace(/\\/g, '/');
		if (!normalizedPath.startsWith('output-docs/') && normalizedPath !== 'output-docs') {
			return true;
		}
		if (!currentUserId) return false;
		const userPrefix = `output-docs/${currentUserId}`;
		return normalizedPath === 'output-docs' || normalizedPath === userPrefix || normalizedPath.startsWith(`${userPrefix}/`);
	});
}

export const MetadataBrowser: React.FC<MetadataBrowserProps> = ({
	orgId,
	workspaceId,
	containerAppUrl,
	selectedPath,
	onPathSelect,
	currentUserId,
}) => {
	const [fileTree, setFileTree] = useState<FileNode[]>([]);
	const [loadingDirs, setLoadingDirs] = useState<Set<string>>(new Set());
	const [fileContent, setFileContent] = useState<string | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [contentLoading, setContentLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [contentError, setContentError] = useState<string | null>(null);

	/**
	 * Phase 1: fetch only the root directory (single-level) so the panel becomes
	 * responsive almost immediately. Directories show with childrenLoaded: false
	 * until the background prefetch or an on-demand expand fills them in.
	 */
	const loadRootLevel = useCallback(async () => {
		if (!orgId || !workspaceId || !containerAppUrl) return;

		setLoading(true);
		setError(null);

		try {
			const response = await apiService.getWorkspaceFiles(containerAppUrl, '.');
			if (response.success && response.data && response.data.files) {
				const filtered = filterOutputDocsForUser(response.data.files, currentUserId);
				setFileTree(buildRootNodes(filtered));
			} else {
				setError(response.error || 'Failed to load file tree');
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to load file tree');
		} finally {
			setLoading(false);
		}
	}, [orgId, workspaceId, containerAppUrl, currentUserId]);

	/**
	 * Phase 2: silently fetch the full recursive tree in the background and merge
	 * it into the already-visible root entries, preserving any expanded state.
	 * Failures are intentionally silent — the tree keeps working via on-demand loads.
	 */
	const prefetchFullTree = useCallback(async () => {
		if (!orgId || !workspaceId || !containerAppUrl) return;

		try {
			const response = await apiService.getWorkspaceFiles(containerAppUrl);
			if (response.success && response.data && response.data.files && response.data.files.length > 0) {
				const filtered = filterOutputDocsForUser(response.data.files, currentUserId);
				const fullTree = buildFileTree(filtered);
				setFileTree(prev => mergeTree(prev, fullTree));
			}
		} catch {
			// Prefetch failures are silent — root-level entries and on-demand loading still work
		}
	}, [orgId, workspaceId, containerAppUrl, currentUserId]);

	/**
	 * Phase 3 (on-demand): fetch a single directory level when the user expands a
	 * folder whose children haven't been loaded yet (i.e. before the prefetch arrives).
	 */
	const loadDirectoryChildren = useCallback(async (dirPath: string) => {
		if (!orgId || !workspaceId || !containerAppUrl) return;

		setLoadingDirs(prev => new Set(prev).add(dirPath));
		try {
			const response = await apiService.getWorkspaceFiles(containerAppUrl, dirPath);
			const children: FileNode[] = response.success && response.data && response.data.files
				? sortWorkspaceFiles(response.data.files).map(file => ({
					name: file.name,
					type: file.type,
					path: file.path,
					children: file.type === 'directory' ? [] : undefined,
					expanded: false,
					childrenLoaded: file.type !== 'directory',
				}))
				: [];
			setFileTree(prev => setNodeChildren(prev, dirPath, children));
		} catch {
			// Mark as loaded with empty children so the user isn't stuck with a spinner
			setFileTree(prev => setNodeChildren(prev, dirPath, []));
		} finally {
			setLoadingDirs(prev => {
				const next = new Set(prev);
				next.delete(dirPath);
				return next;
			});
		}
	}, [orgId, workspaceId, containerAppUrl]);

	const loadFileContent = useCallback(async (path: string) => {
		if (!orgId || !workspaceId || !containerAppUrl) return;

		setContentLoading(true);
		setContentError(null);
		setFileContent(null);

		try {
			const response = await apiService.getWorkspaceFileContent(containerAppUrl, path);
			if (response.success && response.data && response.data.content !== undefined) {
				setFileContent(response.data.content);
			} else {
				setContentError(response.error || 'Failed to load file content');
			}
		} catch (err) {
			setContentError(err instanceof Error ? err.message : 'Failed to load file content');
		} finally {
			setContentLoading(false);
		}
	}, [orgId, workspaceId, containerAppUrl]);

	useEffect(() => {
		const init = async () => {
			await loadRootLevel();
			void prefetchFullTree();
		};
		void init();
	}, [loadRootLevel, prefetchFullTree]);

	useEffect(() => {
		if (selectedPath) {
			loadFileContent(selectedPath);
		}
	}, [selectedPath, loadFileContent]);

	const handleFileClick = (nodePath: string, type: 'file' | 'directory') => {
		if (type === 'file') {
			onPathSelect(nodePath);
		} else {
			setFileTree(prev => updateNodeExpanded(prev, nodePath));
			const node = findNodeByPath(fileTree, nodePath);
			if (node && !node.childrenLoaded) {
				loadDirectoryChildren(nodePath);
			}
		}
	};

	const renderFileTree = (nodes: FileNode[], level = 0): React.ReactNode =>
		nodes.map(node => (
			<React.Fragment key={node.path}>
				<ListItem disablePadding sx={{ pl: level * 2 }}>
					<ListItemButton
						selected={node.type === 'file' && node.path === selectedPath}
						onClick={() => handleFileClick(node.path, node.type)}
						dense
						sx={{
							'&.Mui-selected': {
								backgroundColor: 'action.selected',
							},
						}}
					>
						{node.type === 'directory' ? (
							node.expanded ? (
								<ExpandMoreIcon sx={{ mr: 1, fontSize: '1rem' }} />
							) : (
								<ChevronRightIcon sx={{ mr: 1, fontSize: '1rem' }} />
							)
						) : (
							<Box sx={{ width: 24, mr: 1 }} />
						)}
						{node.type === 'directory' ? (
							<FolderIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'text.secondary' }} />
						) : (
							<FileIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'text.secondary' }} />
						)}
						<ListItemText
							primary={node.name}
							slotProps={{
								primary: {
									fontSize: '0.9rem',
								},
							}}
						/>
						{node.type === 'directory' && loadingDirs.has(node.path) && (
							<CircularProgress size={12} sx={{ ml: 1 }} />
						)}
					</ListItemButton>
				</ListItem>
				{node.type === 'directory' && node.expanded && node.children && !loadingDirs.has(node.path) && (
					<>{renderFileTree(node.children, level + 1)}</>
				)}
			</React.Fragment>
		));

	return (
		<Box sx={{ flex: 1, display: 'flex', height: '100%', overflow: 'hidden' }}>
			{/* File Tree Panel */}
			<Paper
				sx={{
					width: 300,
					borderRight: '1px solid #e0e0e0',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
				}}
			>
				<Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
					<Typography variant="h6" sx={{ fontWeight: 'bold' }}>
						Workspace Files
					</Typography>
				</Box>
				<Box sx={{ flex: 1, overflow: 'auto' }}>
					{loading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
							<CircularProgress size={24} />
						</Box>
					) : error ? (
						<Box sx={{ p: 2 }}>
							<Alert severity="error">{error}</Alert>
						</Box>
					) : (
						<List sx={{ p: 0 }}>
							{renderFileTree(fileTree)}
						</List>
					)}
				</Box>
			</Paper>

			{/* File Content Panel */}
			<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
				<Paper
					sx={{
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						overflow: 'hidden',
						borderRadius: 0,
					}}
				>
					<Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
						<Typography variant="h6" sx={{ fontWeight: 'bold' }}>
							{selectedPath || 'No file selected'}
						</Typography>
					</Box>
					<Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
						{contentLoading ? (
							<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
								<CircularProgress size={24} />
							</Box>
						) : contentError ? (
							<Alert severity="error">{contentError}</Alert>
						) : fileContent !== null ? (
							<Box
								component="pre"
								sx={{
									m: 0,
									p: 2,
									backgroundColor: 'grey.50',
									border: '1px solid #e0e0e0',
									borderRadius: 1,
									overflow: 'auto',
									fontSize: '0.875rem',
									fontFamily: 'monospace',
									whiteSpace: 'pre-wrap',
									wordBreak: 'break-word',
								}}
							>
								{fileContent}
							</Box>
						) : (
							<Typography variant="body2" color="text.secondary">
								Select a file to view its contents
							</Typography>
						)}
					</Box>
				</Paper>
			</Box>
		</Box>
	);
};
